// InstallPrompt — PWA 홈화면 추가 안내 배너 (U70).
//
// 동작:
//   - beforeinstallprompt 이벤트를 캡처해 슬라이드업 카드 노출
//   - "[설치]" 버튼 → deferredPrompt.prompt() 실행 → outcome 확인 → 토스트
//   - "[나중에]" 버튼 → localStorage에 dismiss timestamp 기록 (30일 재노출 억제)
//   - 이미 standalone 모드이면 절대 노출 안 함
//   - iOS Safari fallback: beforeinstallprompt 미지원 환경에서 수동 안내 카드 (1회)
//
// localStorage 키:
//   - locker:install-dismissed  : ISO 날짜 문자열 (dismiss 시각)
//   - locker:ios-prompt-shown   : "1" (iOS 안내 1회 표시 완료 플래그)

import { useEffect, useState, useCallback } from 'react';
import { X, PlusSquare } from 'lucide-react';

// ── 타입 정의 ──────────────────────────────────────────────────

/** beforeinstallprompt 이벤트 타입 (표준 미정의 → 직접 선언) */
interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  prompt(): Promise<void>;
}

/** 배너 상태 — 'hidden' | 'android' | 'ios' */
type PromptState = 'hidden' | 'android' | 'ios';

// ── 상수 ──────────────────────────────────────────────────────

const DISMISS_KEY = 'locker:install-dismissed';
const IOS_SHOWN_KEY = 'locker:ios-prompt-shown';
/** 30일 무시 기간 (ms) */
const DISMISS_DURATION_MS = 30 * 24 * 60 * 60 * 1000;

// ── 헬퍼 함수 ─────────────────────────────────────────────────

/** 현재 standalone(설치된 PWA) 모드인지 확인 */
function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    // iOS Safari: navigator.standalone
    ('standalone' in window.navigator &&
      (window.navigator as Navigator & { standalone?: boolean }).standalone === true)
  );
}

/** iOS Safari 환경인지 확인 (beforeinstallprompt 미지원) */
function isIosSafari(): boolean {
  const ua = navigator.userAgent;
  // iPad OS 13+ 는 Mac UA를 사용하므로 ontouchend로 보완
  const isIos = /iphone|ipad|ipod/i.test(ua) || ('ontouchend' in document && /macintosh/i.test(ua));
  const isSafari = /^((?!chrome|crios|fxios|android).)*safari/i.test(ua);
  return isIos && isSafari;
}

/** dismiss timestamp 읽기 — 30일 이내이면 true (숨겨야 함) */
function isDismissed(): boolean {
  try {
    const stored = localStorage.getItem(DISMISS_KEY);
    if (!stored) return false;
    const dismissedAt = new Date(stored).getTime();
    return Date.now() - dismissedAt < DISMISS_DURATION_MS;
  } catch {
    return false;
  }
}

/** dismiss timestamp 기록 */
function saveDismiss(): void {
  try {
    localStorage.setItem(DISMISS_KEY, new Date().toISOString());
  } catch {
    // localStorage 불가 환경(테스트/iframe) — 무시
  }
}

/** iOS 안내 1회 표시 완료 기록 */
function saveIosShown(): void {
  try {
    localStorage.setItem(IOS_SHOWN_KEY, '1');
  } catch {
    // 무시
  }
}

/** iOS 안내를 이미 표시했는지 확인 */
function isIosAlreadyShown(): boolean {
  try {
    return localStorage.getItem(IOS_SHOWN_KEY) === '1';
  } catch {
    return false;
  }
}

// ── InstallPrompt 컴포넌트 ────────────────────────────────────

/**
 * PWA 설치 유도 배너.
 * Android/Chrome: beforeinstallprompt 이벤트 기반 네이티브 설치 흐름.
 * iOS Safari: 공유 아이콘 → 홈화면 추가 수동 안내 (1회).
 */
export interface InstallPromptProps {
  /**
   * true면 배너를 잠시 숨긴다 (상태는 유지 — 다시 false가 되면 재노출).
   * 상세·등록 패널이 열려 있을 때 모바일에서 저장 버튼을 가리지 않도록 App이 전달 (2026-10-03).
   */
  suppressed?: boolean;
}

export function InstallPrompt({ suppressed = false }: InstallPromptProps = {}) {
  const [state, setState] = useState<PromptState>('hidden');
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  /** 설치 완료/실패 후 짧게 노출하는 피드백 메시지 */
  const [feedback, setFeedback] = useState<string | null>(null);

  useEffect(() => {
    // standalone 모드이면 배너 전혀 노출 안 함
    if (isStandalone()) return;

    // 30일 dismiss 중이면 노출 안 함
    if (isDismissed()) return;

    // iOS Safari fallback: 1회만 노출
    if (isIosSafari()) {
      if (!isIosAlreadyShown()) {
        setState('ios');
        saveIosShown();
      }
      return;
    }

    // Android/Chrome: beforeinstallprompt 이벤트 대기
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setState('android');
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  // ── 설치 버튼 핸들러 ────────────────────────────────────────

  const handleInstall = useCallback(async () => {
    if (!deferredPrompt) return;
    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setFeedback('Locker가 홈 화면에 추가됐어요!');
      } else {
        setFeedback('나중에 언제든지 설치할 수 있어요.');
        saveDismiss();
      }
    } catch {
      setFeedback('설치를 완료하지 못했어요. 나중에 다시 시도해 주세요.');
    } finally {
      setDeferredPrompt(null);
      setState('hidden');
      // 피드백 메시지 3초 후 자동 제거
      setTimeout(() => setFeedback(null), 3000);
    }
  }, [deferredPrompt]);

  // ── 나중에 버튼 핸들러 ──────────────────────────────────────

  const handleDismiss = useCallback(() => {
    saveDismiss();
    setState('hidden');
  }, []);

  // ── iOS 닫기 핸들러 ─────────────────────────────────────────

  const handleIosClose = useCallback(() => {
    setState('hidden');
  }, []);

  // ── 렌더 ────────────────────────────────────────────────────

  // 패널 열림 중에는 배너만 숨김 (설치 피드백 메시지는 짧게 유지되므로 그대로 노출)
  if (suppressed && !feedback) return null;

  return (
    <>
      {/* Android/Chrome 설치 배너 */}
      {state === 'android' && (
        <div
          className="install-prompt install-prompt--android"
          role="dialog"
          aria-labelledby="install-prompt-title"
          data-testid="install-prompt"
        >
          <div className="install-prompt__icon" aria-hidden="true">
            <PlusSquare size={20} />
          </div>
          <div className="install-prompt__body">
            <p id="install-prompt-title" className="install-prompt__message">
              Locker를 홈 화면에 추가하면 앱처럼 사용할 수 있어요
            </p>
            <div className="install-prompt__actions">
              <button
                className="install-prompt__btn install-prompt__btn--install"
                onClick={() => void handleInstall()}
                data-testid="install-btn"
              >
                설치
              </button>
              <button
                className="install-prompt__btn install-prompt__btn--later"
                onClick={handleDismiss}
                data-testid="dismiss-btn"
              >
                나중에
              </button>
            </div>
          </div>
          <button
            className="install-prompt__close"
            onClick={handleDismiss}
            aria-label="설치 배너 닫기"
            data-testid="close-btn"
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* iOS Safari 수동 안내 카드 */}
      {state === 'ios' && (
        <div
          className="install-prompt install-prompt--ios"
          role="dialog"
          aria-labelledby="install-prompt-ios-title"
          data-testid="install-prompt-ios"
        >
          <div className="install-prompt__body">
            <p id="install-prompt-ios-title" className="install-prompt__message">
              Safari 하단의{' '}
              <span className="install-prompt__highlight">공유 아이콘(↑)</span>을 탭한 뒤{' '}
              <span className="install-prompt__highlight">'홈 화면에 추가'</span>를 선택하면
              앱처럼 사용할 수 있어요
            </p>
          </div>
          <button
            className="install-prompt__close"
            onClick={handleIosClose}
            aria-label="안내 닫기"
            data-testid="ios-close-btn"
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* 피드백 토스트 (설치 완료/거부 후) */}
      {feedback && (
        <div
          className="install-prompt__feedback"
          role="status"
          aria-live="polite"
          data-testid="install-feedback"
        >
          {feedback}
        </div>
      )}
    </>
  );
}
